import { ArrayUtils, engineComponent, NumberUtils } from "neo-common";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { Paylines } from "../Paylines";
import { Symbols } from "../Symbols";
import { Machine } from "../Machine";
import { StageController } from "../../Components/StageController";
import { WinAnimator } from "../WinAnimator";
import { FreespinsController } from "./FreespinsController";
import { GameManager } from "../../Managers/GameManager";
import { PlayList } from "../../Components/Sounds/PlayList";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { CustomUtils } from "../../Utils/CustomUtils";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { WinAnimatorFs } from "../../CustomComponents/WinAnimatorFs";
import { BaseView } from "../Base/BaseView";
import { ModuleManager } from "../../Managers/ModuleManager";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";

@engineComponent(ComponentTypes.DisplayObject)
export class FreespinsView extends LayoutContainerElement {
    private _controller: FreespinsController;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorFs;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        StageController.freespinsStageContainer = this;
    }

    create(node: Element): void {
        super.create(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                    this._paylines.addTopContainer(this.childs["PaylinesTop"]);
                    this._paylines.reset();
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimatorFs) {
                    this._winAnimator = this.childs[key];
                }
            }

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    disableLogo(): void {
        if (this.childs["LogoContainer"] != null) {
            this.removeChild(this.childs["LogoContainer"]);
        }
    }

    initReceived(controller: FreespinsController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.freespinsResponse != null) {
                    if (
                        GameManager.freespinsResponse.initData != null &&
                        GameManager.freespinsResponse.initData.reels !== null
                    ) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(this._symbols, GameManager.freespinsResponse.initData.reels);
                    }

                    if (this._winAnimator != null) {
                        this._winAnimator.on(WinAnimator.FinishWinCalculation, this.finishWinCalculation.bind(this));
                        this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                        this._winAnimator.on(
                            WinAnimatorFs.ShowFinalTableFinished,
                            this.finalTableAnimationFinished.bind(this),
                        );
                        this._winAnimator.on(
                            WinAnimatorFs.MultiplierAnimationFinished,
                            this.machineStoppedDelayed.bind(this),
                        );
                        this._winAnimator.initialize(
                            this._symbols,
                            this._paylines,
                            this.childs["NoWinContainer"],
                            this.childs["WinContainer"],
                        );
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no freespin reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.visible = false;
    }

    initIntro(): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;
        StageController.baseStageContainer.visible = false;
        StageController.pickStageContainer.visible = false;

        AmbientBackground.changeAmbientBackground("transition", 0, 0);

        if (GameManager.freespinsResponse.currentWin > 0) {
            if (this.childs["Reels"] != null) this.childs["Reels"].goToCase("WIN");
        }

        if (this.childs["Background"] != null) this.childs["Background"].goToCase("NORMAL");

        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);

        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showInitWins(GameManager.freespinsResponse.wins, GameManager.freespinsResponse.currentWin);

        this.visible = true;

        if (CustomUtils.isLastFreespin()) {
            PlayList.stop("SND_FSLoop");

            this._winAnimator.showScore(true);
            this._winAnimator.showFinalTable(true);
        } else {
            PlayList.loop("SND_FSLoop");
            this._winAnimator.showScore();
        }
    }

    spin(): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_FSLoop");

        if (this.childs["Reels"] != null && this.childs["Reels"].getCurrentCase() === "WIN")
            this.childs["Reels"].goToCase("TO_NORMAL");

        this.updateTotalWin();
        this._winAnimator.updateScore(
            "FsTableFsCount",
            GameManager.freespinsResponse.freeSpins.remainingCount - 1,
            true,
        );
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);

        let isHighWin = this._winAnimator.reset(true);

        this._machine.spinStart(isHighWin, true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    startReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    endReceived(): void {}

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.freespinsResponse.screen.reels, this._machine.actualReelStopped);
    }

    machineStopped(): void {
        if (this._winAnimator.hasNewMultiplier()) {
            if (this.childs["Reels"] != null) this.childs["Reels"].goToCase("MULTIPLIER");
            this._winAnimator.showMultiplierAnimation();
            this._controller.machineStoppedWithAnimation();
        } else {
            this.machineStoppedDelayed();
        }
    }

    machineStoppedDelayed(): void {
        this._controller.machineStopped();

        if (this._winAnimator.showWins(GameManager.freespinsResponse.wins, GameManager.freespinsResponse.currentWin)) {
            PlayList.stop("SND_FSLoop");
        }

        if (GameManager.freespinsResponse.currentWin > 0 && this.childs["Reels"] != null)
            this.childs["Reels"].goToCase("WIN");

        if (GameManager.freespinsResponse.currentWin === 0) {
            if (this.childs["Reels"] != null && this.childs["Reels"].getCurrentCase() === "MULTIPLIER")
                this.childs["Reels"].goToCase("TO_NORMAL");
            if (CustomUtils.isLastFreespin()) {
                /*PlayList.stop('SND_FSLoop');
                PlayList.loop('SND_FSWait');
                PlayList.play('SND_FSEnd');*/

                this._winAnimator.showFinalTable();
            }
        }
    }

    finishWinCalculation(): void {
        this.updateTotalWin();
        this._controller.winCalculationFinished();
    }

    private updateTotalWin(): void {
        let totalWin = GameManager.freespinsResponse.totalWin;
        if (totalWin > 0) {
            if (CustomUtils.isLastFreespin()) {
                const multiplierValue = GameManager.freespinsResponse.freeSpins["customData"]
                    ? GameManager.freespinsResponse.freeSpins["customData"].totalWinMultiplier
                    : 1;
                totalWin = parseFloat(NumberUtils.devide(GameManager.freespinsResponse.totalWin, multiplierValue));
            }

            this._winAnimator.updateScore("FsTableWin", totalWin);
        }
    }

    finalTableAnimationFinished(): void {
        PlayList.loop("SND_FSWait");
        this._controller.finalTableCalculationFinished();
    }

    finishWinAnimator(): void {
        this._controller.allWinsFinished();

        if (CustomUtils.isLastFreespin()) {
            this._winAnimator.showFinalTable();
        } else {
            PlayList.loop("SND_FSLoop");
        }
    }

    intro(long: boolean = false): void {
        StageController.baseStage = false;
        StageController.pickStage = false;
        StageController.freespinStage = true;

        this._winAnimator.reset(true);
        this._winAnimator.resetFinalTable();
        this._paylines.reset();

        this.visible = true;

        this.addChild(BaseView.Transition);
        if (this.childs["Background"] != null) this.childs["Background"].goToCase("NORMAL");
        if (this.childs["Reels"] != null) this.childs["Reels"].goToCase("NORMAL");

        this._winAnimator.showScore();

        this._introDelay.start(30);
    }

    introFinished(): void {
        if (CustomUtils.isLastFreespin()) {
            PlayList.stop("SND_FSLoop");

            if (this.childs["FsMessages"] != null) {
                this.childs["FsMessages"].goToCase("SHOW_FS");
            }
        } else {
            PlayList.loop("SND_FSLoop");
        }

        this._controller.introFinished();
    }

    outro(): void {
        StageController.freespinStage = true;
        this.addChild(BaseView.Transition);

        PlayList.stop("SND_FSLoop");
        PlayList.stop("SND_FSWait");
        PlayList.play("SND_FeatureTransOut");

        BaseView.Transition.goToCase("START");

        this._outroDelay.start(120);
    }

    outroFinished(): void {
        StageController.freespinStage = false;
        if (this.childs["Background"] != null) this.childs["Background"].goToCase("EMPTY");

        this.visible = false;
        this._controller.outroFinished();
    }

    enable(): void {}

    selectPayline(id?: number): void {
        // if (this._paylinesSelector != null) {
        //     this._paylinesSelector.select(id);
        // }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Freespins/FreespinsView.ts
 **/